import { arrayMove } from '@dnd-kit/sortable';

import type { StepDuration, WorkflowMiddleStep, WorkflowStepLocalKey } from '../_types/workflowBuilder';
import type { DurationUnit, RejectBehavior, ReviewWorkflowStep, UpdateReviewWorkflowStepRequest } from '../_types';
import type { WorkflowRejectAction } from '../_types/workflowBuilder';

export function newLocalKey(): WorkflowStepLocalKey {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return `step-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export function createWorkflowMiddleStep(partial?: Partial<WorkflowMiddleStep>): WorkflowMiddleStep {
  return {
    localKey: partial?.localKey ?? newLocalKey(),
    serverStepId: partial?.serverStepId,
    name: partial?.name ?? '',
    reviewerUserIds: partial?.reviewerUserIds ?? [],
    timeAllowed: partial?.timeAllowed,
    rejectAction: partial?.rejectAction,
    timeToResubmit: partial?.timeToResubmit,
  };
}

export function reorderMiddleSteps(
  steps: WorkflowMiddleStep[],
  activeId: WorkflowStepLocalKey,
  overId: WorkflowStepLocalKey
): WorkflowMiddleStep[] {
  const oldIndex = steps.findIndex((s) => s.localKey === activeId);
  const newIndex = steps.findIndex((s) => s.localKey === overId);
  if (oldIndex < 0 || newIndex < 0) {
    return steps;
  }
  return arrayMove(steps, oldIndex, newIndex);
}

export function removeMiddleStepByKey(
  steps: WorkflowMiddleStep[],
  localKey: WorkflowStepLocalKey
): WorkflowMiddleStep[] {
  return steps.filter((s) => s.localKey !== localKey);
}

export function updateMiddleStep(
  steps: WorkflowMiddleStep[],
  localKey: WorkflowStepLocalKey,
  patch: Partial<WorkflowMiddleStep>
): WorkflowMiddleStep[] {
  return steps.map((s) => (s.localKey === localKey ? { ...s, ...patch, localKey: s.localKey } : s));
}

function apiUnitToLocalUnit(unit: DurationUnit): StepDuration['unit'] {
  const map: Record<DurationUnit, StepDuration['unit']> = {
    Days: 'days',
    Hours: 'hours',
    Weeks: 'weeks',
  };
  return map[unit] ?? 'days';
}

function apiBehaviorToRejectAction(behavior: RejectBehavior): WorkflowRejectAction {
  if (behavior === 'PreviousStep') return 'returnToPreviousStep';
  return 'returnToAuthor';
}

export function mapApiStepToMiddleStep(apiStep: ReviewWorkflowStep): WorkflowMiddleStep {
  const hasDeadline = apiStep.deadlineValue != null && apiStep.deadlineUnit != null;
  const hasResubmission =
    apiStep.resubmissionDeadlineValue != null && apiStep.resubmissionDeadlineUnit != null;

  return {
    localKey: newLocalKey(),
    serverStepId: apiStep.reviewWorkflowStepId,
    name: apiStep.stepName ?? '',
    reviewerUserIds: apiStep.reviewerPoolType === 'User' && apiStep.reviewerPoolId
      ? [apiStep.reviewerPoolId]
      : [],
    timeAllowed: hasDeadline
      ? { value: apiStep.deadlineValue!, unit: apiUnitToLocalUnit(apiStep.deadlineUnit!) }
      : undefined,
    rejectAction: apiStep.rejectBehavior ? apiBehaviorToRejectAction(apiStep.rejectBehavior) : undefined,
    timeToResubmit: hasResubmission
      ? {
          value: apiStep.resubmissionDeadlineValue!,
          unit: apiUnitToLocalUnit(apiStep.resubmissionDeadlineUnit!),
        }
      : undefined,
  };
}

function localUnitToApiUnit(unit: StepDuration['unit']): DurationUnit {
  const map: Record<string, DurationUnit> = { days: 'Days', hours: 'Hours', weeks: 'Weeks' };
  return map[unit] ?? 'Days';
}

function rejectActionToApiBehavior(action: WorkflowRejectAction | undefined): RejectBehavior {
  if (action === 'returnToPreviousStep') return 'PreviousStep';
  return 'Resubmission';
}

export function mapMiddleStepToApiStep(
  step: WorkflowMiddleStep,
  stepNumber: number
): UpdateReviewWorkflowStepRequest {
  // timeAllowed and rejectAction are validated before save — non-null assertions are safe here
  const deadlineUnit = localUnitToApiUnit(step.timeAllowed!.unit);
  const resubUnit = step.timeToResubmit ? localUnitToApiUnit(step.timeToResubmit.unit) : null;

  return {
    reviewWorkflowStepId: step.serverStepId ?? 0,
    stepNumber,
    stepName: step.name,
    reviewerPoolType: step.reviewerUserIds.length > 0 ? 'User' : 'AnyReviewer',
    reviewerPoolId: step.reviewerUserIds[0] ?? null,
    deadlineValue: step.timeAllowed!.value,
    deadlineUnit,
    resubmissionDeadlineValue: step.timeToResubmit?.value ?? null,
    resubmissionDeadlineUnit: resubUnit,
    approveBehavior: 'NextStep',
    rejectBehavior: rejectActionToApiBehavior(step.rejectAction),
  };
}
